class LocalStorageService {
  static THEME_MODE_KEY = "randostat:theme-mode";
  static SELECTED_LOTTERY_KEY = "selected_lottery";
  static LEGACY_SELECTED_LOTTERY_KEY = "randostat.selectedLottery";
  static SELECTED_COUNTRY_KEY = "selected_country";
  static LEGACY_SELECTED_COUNTRY_KEY = "randostat:selected-country";
  static DETECTED_COUNTRY_KEY = "detected_country";
  static THEME_MODES = ["light", "dark", "system"];

  getStorage() {
    if (typeof window === "undefined" || !window.localStorage) {
      return null;
    }
    return window.localStorage;
  }

  setItem(key, value) {
    try {
      const serializedValue = JSON.stringify(value);
      const storage = this.getStorage();
      if (!storage) return;
      storage.setItem(key, serializedValue);
    } catch (error) {
      console.error("Error setting item in localStorage", error);
    }
  }

  getItem(key) {
    try {
      const storage = this.getStorage();
      if (!storage) return null;
      const serializedValue = storage.getItem(key);
      if (!serializedValue) {
        return null;
      }

      try {
        return JSON.parse(serializedValue);
      } catch (_parseError) {
        // Backward compatibility for previously stored raw string values.
        return serializedValue;
      }
    } catch (error) {
      console.error("Error getting item from localStorage", error);
      return null;
    }
  }

  removeItem(key) {
    try {
      const storage = this.getStorage();
      if (!storage) return;
      storage.removeItem(key);
    } catch (error) {
      console.error("Error removing item from localStorage", error);
    }
  }

  clear() {
    try {
      const storage = this.getStorage();
      if (!storage) return;
      storage.clear();
    } catch (error) {
      console.error("Error clearing localStorage", error);
    }
  }

  setThemeMode(mode) {
    if (!LocalStorageService.THEME_MODES.includes(mode)) return;
    this.setItem(LocalStorageService.THEME_MODE_KEY, mode);
  }

  getThemeMode() {
    const mode = this.getItem(LocalStorageService.THEME_MODE_KEY);
    return LocalStorageService.THEME_MODES.includes(mode) ? mode : "system";
  }

  setSelectedLotteryId(lotteryId) {
    if (typeof lotteryId !== "string" || lotteryId.trim() === "") {
      return;
    }
    this.setItem(LocalStorageService.SELECTED_LOTTERY_KEY, lotteryId.toLowerCase().trim());
  }

  getSelectedLotteryId() {
    const lotteryId = this.getItem(LocalStorageService.SELECTED_LOTTERY_KEY);
    if (typeof lotteryId === "string") {
      return lotteryId.toLowerCase().trim();
    }

    const legacyLotteryId = this.getItem(LocalStorageService.LEGACY_SELECTED_LOTTERY_KEY);
    if (typeof legacyLotteryId === "string" && legacyLotteryId.trim() !== "") {
      const normalizedLegacyLotteryId = legacyLotteryId.toLowerCase().trim();
      this.setItem(LocalStorageService.SELECTED_LOTTERY_KEY, normalizedLegacyLotteryId);
      return normalizedLegacyLotteryId;
    }

    return "";
  }

  setSelectedCountryCode(countryCode) {
    if (typeof countryCode !== "string") {
      return;
    }

    const normalizedCountryCode = countryCode.toUpperCase().trim();
    if (!/^[A-Z]{2}$/.test(normalizedCountryCode)) {
      return;
    }

    this.setItem(LocalStorageService.SELECTED_COUNTRY_KEY, normalizedCountryCode);
  }

  getSelectedCountryCode() {
    const countryCode = this.getItem(LocalStorageService.SELECTED_COUNTRY_KEY);
    if (typeof countryCode === "string") {
      const normalizedCountryCode = countryCode.toUpperCase().trim();
      if (/^[A-Z]{2}$/.test(normalizedCountryCode)) {
        return normalizedCountryCode;
      }
    }

    const legacyCountryCode = this.getItem(LocalStorageService.LEGACY_SELECTED_COUNTRY_KEY);
    if (typeof legacyCountryCode === "string") {
      const normalizedLegacyCountryCode = legacyCountryCode.toUpperCase().trim();
      if (/^[A-Z]{2}$/.test(normalizedLegacyCountryCode)) {
        this.setItem(LocalStorageService.SELECTED_COUNTRY_KEY, normalizedLegacyCountryCode);
        return normalizedLegacyCountryCode;
      }
    }

    return "";
  }

  setDetectedCountryCode(countryCode) {
    if (typeof countryCode !== "string") {
      return;
    }

    const normalizedCountryCode = countryCode.toUpperCase().trim();
    if (!/^[A-Z]{2}$/.test(normalizedCountryCode)) {
      return;
    }

    this.setItem(LocalStorageService.DETECTED_COUNTRY_KEY, normalizedCountryCode);
  }

  getDetectedCountryCode() {
    const countryCode = this.getItem(LocalStorageService.DETECTED_COUNTRY_KEY);
    if (typeof countryCode !== "string") {
      return "";
    }

    const normalizedCountryCode = countryCode.toUpperCase().trim();
    return /^[A-Z]{2}$/.test(normalizedCountryCode) ? normalizedCountryCode : "";
  }
}

export const localStorageService = new LocalStorageService();
